import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  IoArrowForwardOutline,
  IoCreateOutline,
  IoOpenOutline,
} from "react-icons/io5";
import { listPages } from "../../../api/pagesApi";
import { listSections } from "../../../api/sectionsApi";
import { getApiErrorMessage } from "../../../api/unwrapApiResponse";
import {
  CMS_SERVICE_PAGES,
  SERVICE_SECTION_LABELS,
} from "../../../config/cmsServicePages";
import EmptyState from "../../../components/dashboard/EmptyState";
import ErrorState from "../../../components/dashboard/ErrorState";
import LoadingState from "../../../components/dashboard/LoadingState";

function sectionLabelsForPage(meta) {
  return SERVICE_SECTION_LABELS.filter(
    (section) => !section.infrastructureOnly || meta.hasHostingPlans
  );
}

export default function ServicePagesManager() {
  const [pages, setPages] = useState([]);
  const [sectionsBySlug, setSectionsBySlug] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await listPages();
      const allPages = Array.isArray(data) ? data : [];
      setPages(allPages);

      const servicePages = CMS_SERVICE_PAGES.map((meta) =>
        allPages.find((p) => p.slug === meta.slug)
      ).filter(Boolean);

      const nextSections = {};
      await Promise.all(
        servicePages.map(async (page) => {
          try {
            const sections = await listSections(page.id);
            nextSections[page.slug] = Array.isArray(sections) ? sections : [];
          } catch {
            nextSections[page.slug] = [];
          }
        })
      );
      setSectionsBySlug(nextSections);
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const pagesBySlug = useMemo(() => {
    const map = {};
    pages.forEach((page) => {
      map[page.slug] = page;
    });
    return map;
  }, [pages]);

  if (loading) return <LoadingState message="Loading service pages…" />;
  if (error) return <ErrorState message={error} onRetry={loadData} />;

  const readyCount = CMS_SERVICE_PAGES.filter((meta) => pagesBySlug[meta.slug]).length;

  return (
    <div className="space-y-8">
      <div className="rounded-2xl border border-emerald-200/60 bg-gradient-to-br from-emerald-50 via-white to-slate-50 p-6 sm:p-8">
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-emerald-700">
          Services
        </p>
        <h2 className="mt-2 text-2xl font-semibold text-slate-900 sm:text-3xl">
          Service pages
        </h2>
        <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-600">
          Edit banner, capabilities, process, Why us, and hosting content for each service.
          Each page maps to its own route on the website.
        </p>
        <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-white px-3 py-1 text-xs font-medium text-emerald-800">
          <span className="h-2 w-2 rounded-full bg-emerald-500" />
          {readyCount} of {CMS_SERVICE_PAGES.length} pages ready
        </div>
      </div>

      <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
        {CMS_SERVICE_PAGES.map((meta) => {
          const page = pagesBySlug[meta.slug];
          const expectedSections = sectionLabelsForPage(meta);
          const pageSections = sectionsBySlug[meta.slug] || [];
          const sectionsByKey = Object.fromEntries(
            pageSections.map((section) => [section.key, section])
          );
          const count = pageSections.length;

          return (
            <article
              key={meta.slug}
              className={`group flex flex-col overflow-hidden rounded-2xl border bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-md ${meta.accentBorder}`}
            >
              <div className={`bg-gradient-to-br ${meta.accent} px-5 py-5`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className={`h-2.5 w-2.5 rounded-full ${meta.accentDot}`} />
                      <h3 className="text-lg font-semibold text-slate-900">
                        {meta.name}
                      </h3>
                    </div>
                    <p className="mt-1 text-sm text-slate-600">{meta.description}</p>
                  </div>
                  {page ? (
                    <span
                      className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide ${
                        page.isPublished
                          ? "bg-emerald-100 text-emerald-800"
                          : "bg-amber-100 text-amber-800"
                      }`}
                    >
                      {page.isPublished ? "Live" : "Draft"}
                    </span>
                  ) : (
                    <span className="shrink-0 rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                      Missing
                    </span>
                  )}
                </div>
              </div>

              <div className="flex flex-1 flex-col px-5 py-4">
                <div className="mb-4 flex flex-wrap gap-1.5">
                  {expectedSections.map((section) => {
                    const existing = sectionsByKey[section.key];

                    if (existing && page) {
                      return (
                        <Link
                          key={section.key}
                          to={`/cmsadmin/dashboard/pages/${page.id}/sections/${existing.id}`}
                          className="rounded-lg bg-slate-100 px-2 py-1 text-[11px] font-medium text-slate-700 transition hover:bg-slate-200"
                        >
                          {section.label}
                        </Link>
                      );
                    }

                    return (
                      <span
                        key={section.key}
                        className="rounded-lg bg-slate-100 px-2 py-1 text-[11px] font-medium text-slate-600"
                      >
                        {section.label}
                      </span>
                    );
                  })}
                </div>

                <div className="mt-auto space-y-3 border-t border-slate-100 pt-4">
                  <div className="flex items-center justify-between text-xs text-slate-500">
                    <span>/{meta.slug}</span>
                    {page ? (
                      <span>
                        {count} section{count === 1 ? "" : "s"}
                      </span>
                    ) : null}
                  </div>

                  {page ? (
                    <div className="flex flex-wrap gap-2">
                      <Link
                        to={`/cmsadmin/dashboard/pages/${page.id}/sections`}
                        className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-slate-900 px-3 py-2.5 text-sm font-medium text-white transition hover:bg-slate-800"
                      >
                        <IoCreateOutline className="h-4 w-4" />
                        Edit content
                      </Link>
                      <a
                        href={meta.publicPath}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 px-3 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
                      >
                        <IoOpenOutline className="h-4 w-4" />
                        View
                      </a>
                    </div>
                  ) : (
                    <p className="rounded-xl bg-amber-50 px-3 py-2 text-xs text-amber-800">
                      Page not found in CMS. Restart the backend to run seeds.
                    </p>
                  )}
                </div>
              </div>
            </article>
          );
        })}
      </div>

      {readyCount === 0 ? (
        <EmptyState
          title="No service pages seeded yet"
          description="Run the backend seeder to create Design, Development, Engineering, and other service pages."
        />
      ) : null}

      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">Main services page</h3>
            <p className="mt-1 text-sm text-slate-500">
              Hero, capabilities grid, and CTA on /services
            </p>
          </div>
          {pagesBySlug.services ? (
            <Link
              to={`/cmsadmin/dashboard/pages/${pagesBySlug.services.id}/sections`}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
            >
              Edit services overview
              <IoArrowForwardOutline className="h-4 w-4" />
            </Link>
          ) : null}
        </div>
      </div>
    </div>
  );
}
